import React, { useEffect, useId, useRef } from 'react';
import './Modal.css';

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'textarea:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(', ');

const Modal = ({
  isOpen,
  title,
  children,
  actions,
  onClose,
  closeLabel = 'Close modal',
  containerClassName = '',
  bodyClassName = '',
}) => {
  const modalRef = useRef(null);
  const previousFocusedRef = useRef(null);
  const titleId = useId();

  useEffect(() => {
    if (!isOpen) return undefined;

    previousFocusedRef.current = document.activeElement;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const focusOnOpen = window.requestAnimationFrame(() => {
      const modalNode = modalRef.current;
      if (!modalNode) return;
      const focusable = modalNode.querySelectorAll(FOCUSABLE_SELECTOR);
      if (focusable.length > 0) {
        focusable[0].focus();
      } else {
        modalNode.focus();
      }
    });

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose && onClose();
        return;
      }

      if (e.key !== 'Tab') return;

      const modalNode = modalRef.current;
      if (!modalNode) return;

      const focusable = Array.from(modalNode.querySelectorAll(FOCUSABLE_SELECTOR));
      if (focusable.length === 0) {
        e.preventDefault();
        modalNode.focus();
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement;

      if (e.shiftKey && (active === first || !modalNode.contains(active))) {
        e.preventDefault();
        last.focus();
        return;
      }

      if (!e.shiftKey && (active === last || !modalNode.contains(active))) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      window.cancelAnimationFrame(focusOnOpen);
      document.body.style.overflow = previousOverflow;
      if (previousFocusedRef.current && typeof previousFocusedRef.current.focus === 'function') {
        previousFocusedRef.current.focus();
      }
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className={`modal-container ${containerClassName}`.trim()}
        onClick={(e) => e.stopPropagation()}
        ref={modalRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? titleId : undefined}
        aria-label={title ? undefined : 'Dialog'}
        tabIndex={-1}
      >
        <div className="modal-header">
          {title ? <h3 id={titleId} className="modal-title">{title}</h3> : <span />}
          <button
            type="button"
            className="modal-close-btn"
            aria-label={closeLabel}
            onClick={onClose}
          >
            &times;
          </button>
        </div>
        <div className={`modal-body ${bodyClassName}`.trim()}>
          {children}
        </div>
        {actions && <div className="modal-footer">{actions}</div>}
      </div>
    </div>
  );
};

export default Modal;
